import http from '@/lib/http'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

export type DashboardSearchType = 'orderNumber' | 'email' | 'name'

/**
 * Order numbers always carry digits and names never do, so a digit keeps today's order-number
 * search (partial and prefix-less numbers included); anything with an `@` is an email.
 */
export function getDashboardSearchType(query: string): DashboardSearchType {
  if (query.includes('@')) return 'email'
  if (/\d/.test(query)) return 'orderNumber'
  return 'name'
}

type APIOrderItem = {
  name: string | null
  order_item_hash: string
}

type APIOrder = {
  hash: string
  order_number: string | null
  status: string
  billing_email: string
  created_at: string
  items: Array<APIOrderItem>
}

type GetOrdersResponse = {
  orders: Array<APIOrder>
  count: number
}

export type OrderSearchRow = {
  medRequestId: string
  orderNumber: string | null
  email: string
  status: string
  itemName: string | null
  createdAt: string
}

// Enough orders to cover a customer's history; the table itself decides how many rows to show.
const SEARCH_ORDERS_LIMIT = 50

const SEARCH_ATTRIBUTES: Record<Exclude<DashboardSearchType, 'orderNumber'>, string> = {
  email: JSON.stringify(['billing_email']),
  name: JSON.stringify(['billing_full_name'])
}

const fetchOrderSearchRows = async (
  searchQuery: string,
  searchType: Exclude<DashboardSearchType, 'orderNumber'>,
  signal?: AbortSignal
) => {
  const { orders } = await http.get<GetOrdersResponse, GetOrdersResponse>('/orders', {
    params: {
      searchQuery,
      searchAttributes: SEARCH_ATTRIBUTES[searchType],
      searchMode: 'startsWith',
      limit: SEARCH_ORDERS_LIMIT,
      skipCount: true,
      excludeChainOrders: true,
      excludePaymentPendingOrders: true
    },
    signal
  })

  // One row per order item — each is its own med request, and `/request/:id` opens exactly one.
  return orders.flatMap(order =>
    order.items.map<OrderSearchRow>(item => ({
      medRequestId: item.order_item_hash,
      orderNumber: order.order_number,
      email: order.billing_email,
      status: order.status,
      itemName: item.name,
      createdAt: order.created_at
    }))
  )
}

export const useOrderSearch = (searchQuery: string, searchType: DashboardSearchType) =>
  useQuery({
    queryKey: ['orderSearch', searchType, searchQuery],
    queryFn: ({ signal }) =>
      fetchOrderSearchRows(searchQuery, searchType as Exclude<DashboardSearchType, 'orderNumber'>, signal),
    enabled: !!searchQuery && searchType !== 'orderNumber',
    placeholderData: keepPreviousData,
    staleTime: 30000
  })
